<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3>新增门店</h3>
        <button @click="handleClose" class="close-btn">&times;</button>
      </div>
      
      <div class="modal-body">
        <form @submit.prevent="handleSubmit" class="store-form">
          <div class="form-group">
            <label for="storeName" class="required">门店名称</label>
            <input
              id="storeName"
              v-model="formData.storeName"
              type="text"
              placeholder="请输入门店名称"
              class="form-input"
              :class="{ 'error': errors.storeName }"
              required
            />
            <span v-if="errors.storeName" class="error-message">{{ errors.storeName }}</span>
          </div>
          
          <div class="form-group">
            <label for="city">城市</label>
            <input
              id="city"
              v-model="formData.city"
              type="text"
              placeholder="例如：达州市"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="address" class="required">门店地址</label>
            <input
              id="address"
              v-model="formData.address"
              type="text"
              placeholder="请输入门店地址"
              class="form-input"
              :class="{ 'error': errors.address }"
              required
            />
            <span v-if="errors.address" class="error-message">{{ errors.address }}</span>
          </div>

          <div class="form-group">
            <label>门店图片</label>
            <input ref="storeImageInput" type="file" accept="image/*" multiple class="hidden-file-input" @change="handleStoreImagesSelected" />
            <button type="button" class="upload-btn" :disabled="imageUploading || formData.storeImageUrls.length >= 10" @click="$refs.storeImageInput.click()">
              {{ imageUploading ? '上传中...' : '上传门店图片' }}
            </button>
            <div class="form-help">最多上传 10 张，可删除后重新上传实现替换。</div>
            <div class="image-gallery-editor" v-if="formData.storeImageUrls.length">
              <div class="image-editor-item" v-for="(image, index) in formData.storeImageUrls" :key="image">
                <img :src="image" class="image-preview" loading="lazy" />
                <div class="image-actions">
                  <button type="button" class="mini-btn" :disabled="index === 0" @click="moveStoreImage(index, -1)">前移</button>
                  <button type="button" class="mini-btn danger" @click="removeStoreImage(index)">删除</button>
                </div>
              </div>
            </div>
          </div>

          <div class="location-grid">
            <div class="form-group">
              <label for="latitude">纬度</label>
              <input
                id="latitude"
                v-model="formData.latitude"
                type="text"
                placeholder="例如：30.659462"
                class="form-input"
                :class="{ 'error': errors.latitude }"
              />
              <span v-if="errors.latitude" class="error-message">{{ errors.latitude }}</span>
            </div>

            <div class="form-group">
              <label for="longitude">经度</label>
              <input
                id="longitude"
                v-model="formData.longitude"
                type="text"
                placeholder="例如：104.065735"
                class="form-input"
                :class="{ 'error': errors.longitude }"
              />
              <span v-if="errors.longitude" class="error-message">{{ errors.longitude }}</span>
            </div>
          </div>
          
          <div class="form-group">
            <label for="adminIds">门店管理员</label>
            <div class="multi-select-container">
              <div class="multi-select-display" :class="{ 'error': errors.adminIds }" @click="toggleAdminDropdown">
                <span v-if="selectedAdmins.length === 0" class="placeholder">可不选择门店管理员</span>
                <span v-else class="selected-items">
                  {{ selectedAdmins.map(admin => formatAdminDisplayName(admin)).join(', ') }}
                </span>
                <span class="dropdown-arrow" :class="{ 'open': showAdminDropdown }">▼</span>
              </div>
              <div v-if="showAdminDropdown" class="multi-select-dropdown">
                <div class="dropdown-search">
                  <input
                    v-model="adminSearchKeyword"
                    type="text"
                    placeholder="搜索管理员..."
                    class="search-input"
                    @click.stop
                  />
                </div>
                <div class="dropdown-options">
                  <div
                    v-for="admin in filteredAdmins"
                    :key="admin.id"
                    class="dropdown-option"
                    :class="{ 'selected': isAdminSelected(admin.id) }"
                    @click="toggleAdminSelection(admin)"
                  >
                    <input
                      type="checkbox"
                      :checked="isAdminSelected(admin.id)"
                      @click.stop
                    />
                    <span>{{ formatAdminOptionLabel(admin) }}</span>
                  </div>
                  <div v-if="filteredAdmins.length === 0" class="no-options">
                    暂无数据
                  </div>
                </div>
              </div>
            </div>
            <span v-if="errors.adminIds" class="error-message">{{ errors.adminIds }}</span>
          </div>
          
          <div class="form-group">
            <label for="description">门店描述</label>
            <textarea
              id="description"
              v-model="formData.description"
              placeholder="请输入门店描述（可选）"
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>

          <div class="form-tip">门店创建后，价格档位和充值套餐请到小程序“经营配置”中维护。</div>

          <div class="form-group">
            <label for="rechargeOptionDetails">初始会员档详情</label>
            <textarea
              id="rechargeOptionDetails"
              v-model="formData.rechargeOptionDetails"
              placeholder="每行一档，推荐格式：档位名|实付金额|赠送余额"
              class="form-textarea"
              rows="4"
              maxlength="2000"
            ></textarea>
            <div class="char-count">{{ formData.rechargeOptionDetails ? formData.rechargeOptionDetails.length : 0 }}/2000</div>
          </div>

          <div class="form-group">
            <label for="smartPlugCycleEnabled">是否启用空闲循环断电</label>
            <select id="smartPlugCycleEnabled" v-model="formData.smartPlugCycleEnabled" class="form-select">
              <option :value="false">关闭</option>
              <option :value="true">开启</option>
            </select>
            <div class="form-help">开启后，本门店所有已配置 MQTT 的智能插排，在设备空闲且命中生效星期时执行循环断电。</div>
          </div>

          <div class="form-group" v-if="formData.smartPlugCycleEnabled">
            <label>生效星期</label>
            <div class="weekday-checkboxes">
              <label v-for="item in cycleWeekdayOptions" :key="item.value" class="weekday-option">
                <input type="checkbox" :value="item.value" v-model="formData.smartPlugCycleWeekdays" />
                <span>{{ item.label }}</span>
              </label>
            </div>
            <span v-if="errors.smartPlugCycleWeekdays" class="error-message">{{ errors.smartPlugCycleWeekdays }}</span>
            <div class="form-help">上机中设备不会被空闲循环断电影响。</div>
          </div>

          <div class="form-group">
            <label for="keFuPhone">客服热线号码</label>
            <input
              id="keFuPhone"
              v-model="formData.keFuPhone"
              type="text"
              placeholder="请输入客服热线号码"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="customerServiceLabel">客服热线标题</label>
            <input
              id="customerServiceLabel"
              v-model="formData.customerServiceLabel"
              type="text"
              placeholder="请输入客服热线标题"
              class="form-input"
              :class="{ 'error': errors.customerServiceLabel }"
            />
            <span v-if="errors.customerServiceLabel" class="error-message">{{ errors.customerServiceLabel }}</span>
          </div>
        </form>
      </div>
      
      <div class="modal-footer">
        <button @click="handleClose" class="cancel-btn">取消</button>
        <button @click="handleSubmit" class="submit-btn" :disabled="loading">
          {{ loading ? '保存中...' : '保存' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'AddStoreModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'success'],
  data() {
    return {
      loading: false,
      imageUploading: false,
      formData: {
        storeName: '',
        address: '',
        city: '',
        storeImageUrls: [],
        latitude: '',
        longitude: '',
        description: '',
        gongGao: '',
        rechargeDiscountInformation: '',
        playNotice: '',
        rechargeOptionDetails: '',
        smartPlugCycleEnabled: false,
        smartPlugCycleWeekdays: ['1', '2', '3', '4', '5', '6', '7'],
        keFuPhone: '',
        customerServiceLabel: '客服热线'
      },
      errors: {},
      adminList: [],
      selectedAdmins: [],
      showAdminDropdown: false,
      adminSearchKeyword: '',
      cycleWeekdayOptions: [
        { label: '周一', value: '1' },
        { label: '周二', value: '2' },
        { label: '周三', value: '3' },
        { label: '周四', value: '4' },
        { label: '周五', value: '5' },
        { label: '周六', value: '6' },
        { label: '周日', value: '7' }
      ]
    }
  },
  computed: {
    filteredAdmins() {
      const keyword = this.adminSearchKeyword.trim().toLowerCase()
      if (!keyword) {
        return this.adminList
      }
      return this.adminList.filter(admin => {
        const fields = [
          admin.nickname,
          admin.username,
          admin.phone,
          admin.realName
        ]
        return fields.some(field => (field || '').toLowerCase().includes(keyword))
      })
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm()
        this.loadAdminList()
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick)
  },
  methods: {
    resetForm() {
      this.formData = {
        storeName: '',
        address: '',
        city: '',
        storeImageUrls: [],
        latitude: '',
        longitude: '',
        description: '',
        gongGao: '',
        rechargeDiscountInformation: '',
        playNotice: '',
        rechargeOptionDetails: '',
        smartPlugCycleEnabled: false,
        smartPlugCycleWeekdays: ['1', '2', '3', '4', '5', '6', '7'],
        keFuPhone: '',
        customerServiceLabel: '客服热线'
      }
      this.errors = {}
      this.loading = false
      this.selectedAdmins = []
      this.showAdminDropdown = false
      this.adminSearchKeyword = ''
    },
    
    validateForm() {
      this.errors = {}
      let isValid = true
      
      if (!this.formData.storeName.trim()) {
        this.errors.storeName = '请输入门店名称'
        isValid = false
      }
      
      if (!this.formData.address.trim()) {
        this.errors.address = '请输入门店地址'
        isValid = false
      }
      if (this.formData.latitude.trim() && !Number.isFinite(Number(this.formData.latitude))) {
        this.errors.latitude = '纬度必须是数字'
        isValid = false
      }
      if (this.formData.longitude.trim() && !Number.isFinite(Number(this.formData.longitude))) {
        this.errors.longitude = '经度必须是数字'
        isValid = false
      }
      if (!this.formData.customerServiceLabel.trim()) {
        this.errors.customerServiceLabel = '请输入客服热线标题'
        isValid = false
      }
      if (this.formData.smartPlugCycleEnabled && this.formData.smartPlugCycleWeekdays.length === 0) {
        this.errors.smartPlugCycleWeekdays = '请至少选择一个生效星期'
        isValid = false
      }
      
      return isValid
    },
    
    generateStoreCode() {
      // 生成32位数字随机字符串
      let result = ''
      for (let i = 0; i < 32; i++) {
        result += Math.floor(Math.random() * 10)
      }
      return result
    },
    
    async handleSubmit() {
      if (!this.validateForm()) {
        return
      }
      
      this.loading = true
      
      try {
        const response = await request.post('/api/stores/createStore', {
          storeCode: this.generateStoreCode(),
          storeName: this.formData.storeName.trim(),
          address: this.formData.address.trim(),
          city: this.formData.city.trim() || null,
          storeImageUrls: this.formData.storeImageUrls.slice(0, 10),
          latitude: this.parseCoordinate(this.formData.latitude),
          longitude: this.parseCoordinate(this.formData.longitude),
          description: this.formData.description.trim(),
          gongGao: this.formData.gongGao.trim() || null,
          rechargeDiscountInformation: this.formData.rechargeDiscountInformation.trim() || null,
          playNotice: this.formData.playNotice.trim() || null,
          rechargeOptionDetails: this.formData.rechargeOptionDetails.trim() || null,
          smartPlugCycleEnabled: this.formData.smartPlugCycleEnabled,
          smartPlugCycleWeekdays: this.formData.smartPlugCycleWeekdays.join(','),
          keFuPhone: this.formData.keFuPhone.trim() || null,
          customerServiceLabel: this.formData.customerServiceLabel.trim(),
          adminIds: this.selectedAdmins.map(admin => admin.id)
        })
        
        if (response.data.success && response.data.code === 200) {
          this.$emit('success', response.data.data)
          this.handleClose()
        } else {
          throw new Error(response.data.message || '新增门店失败')
        }
        
      } catch (error) {
        console.error('新增门店失败:', error)
        let errorMessage = '新增门店失败'
        
        if (error.response) {
          errorMessage = error.response.data.message || `服务器错误: ${error.response.status}`
        } else if (error.request) {
          errorMessage = '网络连接失败，请检查网络或服务器状态'
        } else {
          errorMessage = error.message || '新增门店失败，请重试'
        }
        
        alert(errorMessage)
      } finally {
        this.loading = false
      }
    },

    async handleStoreImagesSelected(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (!files.length) return
      const remaining = 10 - this.formData.storeImageUrls.length
      if (remaining <= 0) {
        alert('门店图片最多上传10张')
        return
      }
      this.imageUploading = true
      try {
        for (const file of files.slice(0, remaining)) {
          const formData = new FormData()
          formData.append('file', file)
          const response = await request.post('/api/stores/upload-image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          if (response.data.success && response.data.code === 200) {
            this.formData.storeImageUrls.push(response.data.data)
          } else {
            throw new Error(response.data.message || '上传门店图片失败')
          }
        }
      } catch (error) {
        alert(error.response?.data?.message || error.message || '上传门店图片失败')
      } finally {
        this.imageUploading = false
      }
    },

    removeStoreImage(index) {
      this.formData.storeImageUrls.splice(index, 1)
    },

    moveStoreImage(index, direction) {
      const targetIndex = index + direction
      if (targetIndex < 0 || targetIndex >= this.formData.storeImageUrls.length) return
      const [image] = this.formData.storeImageUrls.splice(index, 1)
      this.formData.storeImageUrls.splice(targetIndex, 0, image)
    },

    parseCoordinate(value) {
      const raw = String(value || '').trim()
      if (!raw) {
        return null
      }
      const number = Number(raw)
      return Number.isFinite(number) ? number : null
    },
    
    handleClose() {
      this.$emit('close')
    },

    async loadAdminList() {
      try {
        const response = await request.get('/api/users/getUsers', {
          params: {
            page: 0,
            size: 5000,
            role: 'STORE_ADMIN'
          }
        })

        if (response.data.success && response.data.code === 200) {
          this.adminList = response.data.data.content || []
        } else {
          throw new Error(response.data.message || '获取管理员列表失败')
        }
      } catch (error) {
        console.error('获取管理员列表失败:', error)
        this.adminList = []
      }
    },

    toggleAdminDropdown() {
      this.showAdminDropdown = !this.showAdminDropdown
      if (this.showAdminDropdown) {
        this.adminSearchKeyword = ''
      }
    },

    toggleAdminSelection(admin) {
      const index = this.selectedAdmins.findIndex(selected => selected.id === admin.id)
      if (index > -1) {
        this.selectedAdmins.splice(index, 1)
      } else {
        this.selectedAdmins.push(admin)
      }
    },

    isAdminSelected(adminId) {
      return this.selectedAdmins.some(admin => admin.id === adminId)
    },

    formatAdminDisplayName(admin) {
      return admin.nickname || admin.realName || admin.username || `ID:${admin.id}`
    },

    formatAdminOptionLabel(admin) {
      const name = this.formatAdminDisplayName(admin)
      const extras = [admin.username, admin.phone].filter(value => value && value !== name)
      return extras.length > 0 ? `${name} (${extras.join(' / ')})` : name
    },

    handleDocumentClick(event) {
      if (this.showAdminDropdown && !event.target.closest('.multi-select-container')) {
        this.showAdminDropdown = false
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #666;
}

.modal-body {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.store-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-group label.required::after {
  content: ' *';
  color: #ff4d4f;
}

.form-tip {
  padding: 12px;
  background: #f0f5ff;
  color: #1d4ed8;
  border-radius: 6px;
  font-size: 13px;
}

.form-help {
  color: #666;
  font-size: 12px;
  line-height: 1.5;
}

.location-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.hidden-file-input {
  display: none;
}

.upload-btn {
  align-self: flex-start;
  padding: 8px 16px;
  border: 1px solid #1890ff;
  border-radius: 6px;
  background: #1890ff;
  color: white;
  cursor: pointer;
}

.upload-btn:disabled {
  background: #d9d9d9;
  border-color: #d9d9d9;
  cursor: not-allowed;
}

.image-gallery-editor {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.image-editor-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;
  background: #fafafa;
}

.image-preview {
  width: 100%;
  height: 90px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
}

.image-actions {
  display: flex;
  gap: 6px;
  margin-top: 8px;
}

.mini-btn {
  flex: 1;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 6px;
}

.mini-btn.danger {
  color: #ef4444;
}

.weekday-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
}

.weekday-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #333;
  font-size: 13px;
  cursor: pointer;
}

.form-input, .form-textarea {
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  font-family: inherit;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.form-input.error, .form-textarea.error {
  border-color: #ff4d4f;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.error-message {
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
}

.char-count {
  color: #999;
  font-size: 12px;
  text-align: right;
  margin-top: 4px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.cancel-btn, .submit-btn {
  padding: 8px 20px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.cancel-btn {
  background: white;
  color: #666;
}

.cancel-btn:hover {
  background: #f5f5f5;
  border-color: #40a9ff;
}

.submit-btn {
  background: #1890ff;
  color: white;
  border-color: #1890ff;
}

.submit-btn:hover:not(:disabled) {
  background: #40a9ff;
  border-color: #40a9ff;
}

.submit-btn:disabled {
  background: #d9d9d9;
  border-color: #d9d9d9;
  cursor: not-allowed;
  color: #999;
}

/* 多选下拉框样式 */
.multi-select-container {
  position: relative;
  width: 100%;
}

.multi-select-display {
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 20px;
  transition: border-color 0.3s ease;
}

.multi-select-display:hover {
  border-color: #40a9ff;
}

.multi-select-display:focus-within {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.multi-select-display.error {
  border-color: #ff4d4f;
}

.placeholder {
  color: #999;
}

.selected-items {
  color: #333;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-arrow {
  color: #999;
  font-size: 12px;
  transition: transform 0.3s ease;
  margin-left: 8px;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 200px;
  overflow: hidden;
}

.dropdown-search {
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.dropdown-search .search-input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
}

.dropdown-search .search-input:focus {
  outline: none;
  border-color: #1890ff;
}

.dropdown-options {
  max-height: 150px;
  overflow-y: auto;
}

.dropdown-option {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s ease;
}

.dropdown-option:hover {
  background: #f5f5f5;
}

.dropdown-option.selected {
  background: #e6f7ff;
  color: #1890ff;
}

.dropdown-option input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
}

.no-options {
  padding: 12px;
  text-align: center;
  color: #999;
  font-size: 12px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 20px;
  }
  
  .modal-header, .modal-body, .modal-footer {
    padding: 16px;
  }
  
  .modal-footer {
    flex-direction: column;
  }

  .location-grid {
    grid-template-columns: 1fr;
  }
  
  .cancel-btn, .submit-btn {
    width: 100%;
  }
}
</style>
